"use client";

import { useCallback, useEffect } from "react";

export const normalizeWistiaThumbnailUrl = (url?: string | null) => {
    if (!url) {
        return undefined;
    }

    if (!url.includes("wistia.com") && !url.includes("wi.st")) {
        return url;
    }

    const absoluteUrl = url.startsWith("//") ? `https:${url}` : url;

    try {
        const parsed = new URL(absoluteUrl);
        parsed.searchParams.delete("image_crop_resized");
        return parsed.toString();
    } catch {
        return absoluteUrl;
    }
};

export const getWistiaSwatchUrl = (hashedId?: string | null) => {
    if (!hashedId) {
        return undefined;
    }

    return `https://fast.wistia.com/embed/medias/${hashedId}/swatch`;
};

export const getHeroPosterUrl = ({
    hashedId,
    thumbnailUrl,
    imageUrl,
    imageMobileUrl,
    isMobile,
}: {
    hashedId?: string | null;
    thumbnailUrl?: string | null;
    imageUrl?: string | null;
    imageMobileUrl?: string | null;
    isMobile: boolean;
}) => {
    const cmsImageUrl = isMobile ? (imageMobileUrl ?? imageUrl) : imageUrl;

    return (
        normalizeWistiaThumbnailUrl(cmsImageUrl) ??
        normalizeWistiaThumbnailUrl(thumbnailUrl) ??
        getWistiaSwatchUrl(hashedId)
    );
};

export const hideWistiaClickForSound = (containerId: string) => {
    const container = document.getElementById(containerId);
    if (!container) {
        return false;
    }

    let updated = false;

    for (const node of container.querySelectorAll(
        '.click-for-sound-btn, button[aria-label="Click for sound"]'
    )) {
        if (node instanceof HTMLElement) {
            node.style.display = "none";
            node.setAttribute("aria-hidden", "true");
            updated = true;
        }
    }

    return updated;
};

export const useHeroVideoMute = (
    isVideoMuted: boolean,
    setIsVideoMuted: (muted: boolean) => void,
    videoPlayerId: string
) => {
    const toggleMute = useCallback(() => {
        setIsVideoMuted(!isVideoMuted);
        hideWistiaClickForSound(videoPlayerId);
    }, [isVideoMuted, setIsVideoMuted, videoPlayerId]);

    return { toggleMute };
};

type HeroMediaToggleProps = {
    enabled: boolean;
    isPlaying: boolean;
    playLabel: string;
    pauseLabel: string;
    onToggle: () => void;
};

export const getHeroMediaToggleProps = ({
    enabled,
    isPlaying,
    playLabel,
    pauseLabel,
    onToggle,
}: HeroMediaToggleProps) => {
    if (!enabled) {
        return {};
    }

    return {
        role: "button" as const,
        tabIndex: 0,
        cursor: "pointer" as const,
        "aria-label": isPlaying ? pauseLabel : playLabel,
        "aria-pressed": isPlaying,
        onClick: onToggle,
        onKeyDown: (event: { key: string; preventDefault: () => void }) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onToggle();
            }
        },
    };
};

export const removeEmbeddedMediaFromTabOrder = (containerId: string) => {
    const container = document.getElementById(containerId);
    if (!container) {
        return false;
    }

    let updated = false;

    for (const node of container.querySelectorAll("iframe, video")) {
        if (node instanceof HTMLElement) {
            node.tabIndex = -1;
            updated = true;
        }
    }

    return updated;
};

/** Wistia injects iframes asynchronously. Observe until embedded media is found, then disconnect. */
export const observeEmbeddedMediaTabOrder = (containerId: string) => {
    const container = document.getElementById(containerId);
    if (!container) {
        return undefined;
    }

    const syncPlayerChrome = () => {
        hideWistiaClickForSound(containerId);
        return removeEmbeddedMediaFromTabOrder(containerId);
    };

    if (syncPlayerChrome()) {
        return undefined;
    }

    const observer = new MutationObserver(() => {
        if (syncPlayerChrome()) {
            observer.disconnect();
        }
    });

    observer.observe(container, { childList: true, subtree: true });

    const timeoutId = window.setTimeout(() => observer.disconnect(), 5000);

    return () => {
        observer.disconnect();
        window.clearTimeout(timeoutId);
    };
};

export const useHeroVideoTabOrder = (containerId: string, enabled: boolean) => {
    useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        return observeEmbeddedMediaTabOrder(containerId);
    }, [containerId, enabled]);
};
